import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Entry } from '../../lib/api';
import { useEntriesStore } from '../../stores';
import { MarkdownViewer } from './MarkdownViewer';

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric'
};

function formatDate(dateString: string) {
  return new Intl.DateTimeFormat(undefined, DATE_FORMAT_OPTIONS).format(new Date(dateString));
}

function PencilIcon() {
  return (
    <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  );
}


function EntryMeta({ entry }: { entry: Entry }) {
  return (
    <div className="space-y-2 pt-1">
      {entry.tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {entry.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded-sm bg-paper-200 px-2.5 py-0.5 text-xs font-medium text-ink-600"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {entry.locationName && (
        <div className="flex items-center gap-1.5 text-sm text-ink-500">
          <svg aria-hidden="true" className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
          <span>{entry.locationName}</span>
        </div>
      )}

    </div>
  );
}

interface EntryPresentationProps {
  entry: Entry;
}

function toInputDate(isoString: string): string {
  const d = new Date(isoString);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function EntryPresentation({ entry }: EntryPresentationProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateEntry } = useEntriesStore();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pendingDate, setPendingDate] = useState('');
  const [isSavingDate, setIsSavingDate] = useState(false);

  const handleDateClick = () => {
    setPendingDate(toInputDate(entry.entryDate));
    setShowDatePicker(true);
  };

  const handleDateSave = async () => {
    if (!pendingDate) return;
    setIsSavingDate(true);
    try {
      const original = new Date(entry.entryDate);
      const [year, month, day] = pendingDate.split('-').map(Number);
      const newDate = new Date(original);
      newDate.setFullYear(year, month - 1, day);
      await updateEntry(entry.id, {
        content: entry.content,
        entryDate: newDate.toISOString(),
      });
      setShowDatePicker(false);
    } catch {
      // error handled by store
    } finally {
      setIsSavingDate(false);
    }
  };

  return (
    <div className="card space-y-4">
      {/* Header: clickable date + pencil navigates to edit */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              type="button"
              onClick={handleDateClick}
              className="text-sm text-ink-400 hover:text-primary-600 transition-colors"
            >
              <time dateTime={entry.entryDate}>{formatDate(entry.entryDate)}</time>
            </button>

            {showDatePicker && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowDatePicker(false)}
                />
                <div className="absolute top-full left-0 z-20 mt-1.5 rounded border border-paper-300 bg-paper-50 p-3 shadow-sm space-y-3 min-w-[200px]">
                  <input
                    type="date"
                    className="input w-full"
                    value={pendingDate}
                    onChange={(e) => setPendingDate(e.target.value)}
                    autoFocus
                  />
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => setShowDatePicker(false)}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      className="btn-primary btn-sm"
                      onClick={handleDateSave}
                      disabled={isSavingDate || !pendingDate}
                    >
                      {t('common.save')}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          {entry.wordCount != null && entry.wordCount > 0 && (
            <span className="text-xs text-ink-400">{t('entries.wordCount', { count: entry.wordCount })}</span>
          )}
        </div>

        <button
          type="button"
          onClick={() => navigate(`/entries/${entry.id}/edit`)}
          className="rounded-sm p-1 text-ink-300 hover:text-ink-600 transition-colors"
          aria-label={t('common.edit')}
        >
          <PencilIcon />
        </button>
      </div>

      <h2 className="text-xl font-semibold text-ink break-words">
        <Link to={`/entries/${entry.id}/view`} className="hover:text-primary-600 transition-colors">
          {entry.title}
        </Link>
      </h2>

      <MarkdownViewer key={entry.id} content={entry.content} />

      <EntryMeta entry={entry} />
    </div>
  );
}
